import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import cn from "classnames";
import { Col, Container, Row } from "reactstrap";
import { BOARD_CARD_LOADING } from "../../constants/loading.constant";
import {
  BOARD_CARD_MODAL,
  CHAT_FILTERS_MODAL,
  CONTACT_EXPAND_EDIT_FORM,
  EMOJI_FORM,
  TEMPLATE_CHAT_FORM,
} from "../../constants/modal.constant";
import { useContactCore } from "../../cores/contacts";
import { useGlobalCore } from "../../cores/globals";
import { useMeCore } from "../../cores/me";
import { isFacebookMessagingConnected, isInstagramMessagingConnected, isSocialMessagingConnected } from "../../cores/socialPost/config";
import { useFacebookCore } from "../../cores/facebook";
import { useRouterCore } from "../../cores/router";
import { useWebChatCore } from "../../cores/conversations";
import PanelChatTitle from "../component/chat/PanelChatTitle";
import WebChatForm from "../component/chat/web/WebChatForm";
import SocialChatForm from "../component/chat/social/SocialChatForm";
import EditContactForm from "../component/contacts/modal/EditContactForm";
import { NoData } from "../component/NoData";
import WebChatEmptyState from "../component/chat/WebChatEmptyState";
import Content from "../layout/Content";
import Layout from "../layout/Layout";
import SideModal from "../layout/SideModal";
import TaskSideModals from "../component/tasks/TaskSideModals";
import ContactSideModals from "../component/contacts/ContactSideModals";
import CompanySideModals from "../component/contactCompanies/CompanySideModals";
import EventSideModals from "../component/event/EventSideModals";
import EmailSideModals from "../component/gmail/EmailSideModals";
import BoardSideModals from "../component/boards/BoardSideModals";
import { useConfigCore } from "../../cores/config";
import { Errors } from "../../constants/errors";
import { CARD_PRODUCT } from "../../constants/internalModal.constant";
import { useBoardCore } from "../../cores/boards";
import { ConversationList } from "../component/chat/ConversationList";
import { Position } from "../../cores/globals/interfaces/globalEntity";
import TemplateChatForm from "../component/chat/modals/TemplateChatForm";
import { useTemplateCore } from "../../cores/templates";
import { useSocialChatCore } from "../../cores/socialChat/index";
import { useSocketCore } from "../../cores/socket";
import ChatBarFilter from "../component/chat/chatBarFilter/ChatBarFilter";
import { Conversations } from "../../cores/socialChat/interfaces/conversations-group.interface";
import { ConversationContact } from "../../cores/socialChat/interfaces/conversations.interface";
import { pickBestAvatar } from "../../cores/socialChat/utils/avatar-url";
import { SOCIAL_CHAT_FILTERS_MODAL } from "../../constants/modal.constant";
import ChatFilterModal from "../component/chat/modals/ChatFilterModal";
import { useTaskCore } from "../../cores/tasks";
import { useIntl } from "react-intl";
import WebChatInboxToolbar, {
  WebChatListFilter,
} from "../component/chat/WebChatInboxToolbar";
import "../component/chat/WebChatInbox.scss";

export default function WebChat() {
  const intl = useIntl();
  const {
    company,
    companyPlan,
    companyRole,
    isAdmin,
    isReseller,
    socialSettings,
    getMeSocialSettings,
  } = useMeCore();
  const { settings: facebookSettings, get: getFacebookSettings } =
    useFacebookCore();
  const facebookMessagingConnected =
    isFacebookMessagingConnected(socialSettings) ||
    Boolean(facebookSettings?.token);
  const {
    selected,
    getConversations,
    selectWebConversation,
    conversations: webConversations,
  } = useWebChatCore();

  const {
    conversationStatus,
    selectedSocial,
    facebookMessages,
    instagramConversations,
    facebookConversations,
    whatsappConversations,
    facebookSelected,
    instagramMessages,
    instagramSelected,
    whatsappMessages,
    whatsappSelected,
    linkedinConversations,
    linkedinSelected,
    linkedinMessages,
    linkedinBusinessConversations,
    linkedinBusinessSelected,
    linkedinBusinessMessages,
    selectFacebookConversation,
    selectInstagramConversation,
    selectWhatsappConversation,
    selectLinkedinConversation,
    selectLinkedinBusinessConversation,
    getFacebookConversations,
    getInstagramConversations,
    getWhatsappConversations,
    getLinkedinConversations,
    getLinkedinBusinessConversations,
    getFacebookMessages,
    postFacebookMessage,
    postFacebookSeenMessage,
    getInstagramMessages,
    syncInstagramInboxSilent,
    postInstagramMessage,
    getLinkedinMessages,
    postLinkedinMessage,
    getLinkedinBusinessMessages,
    postLinkedinBusinessMessage,
    getWhatsappMessages,
    postWhatsappMessage,
  } = useSocialChatCore();

  const { disconnected } = useSocketCore();
  const wasSocketDisconnected = useRef(disconnected);

  const { getCommentTypes } = useConfigCore();
  const { goToSocial } = useRouterCore();
  const { getBusinessBoards, cleanCard, errorCode } = useBoardCore();
  const {
    deleteContacts,
    getContactTags,
    getContactStates,
    getContact,
    contactStateRequest,
  } = useContactCore();
  const { getTasksByContact } = useTaskCore();
  const { getChatTemplates } = useTemplateCore();
  const [filter, setFilter] = useState("");
  const [listFilter, setListFilter] = useState<WebChatListFilter>("all");
  const openFiltersModal = () =>
    turnOnModal(CHAT_FILTERS_MODAL, true, Position.RIGHT);

  const getAllConversations = useCallback(() => {
    const active = [
      ...webConversations.active,
      ...instagramConversations.active,
      ...facebookConversations.active,
      ...linkedinConversations.active,
      ...linkedinBusinessConversations.active,
      ...whatsappConversations.active,
    ];
    const archived = [
      ...webConversations.archived,
      ...instagramConversations.archived,
      ...facebookConversations.archived,
      ...linkedinConversations.archived,
      ...linkedinBusinessConversations.archived,
      ...whatsappConversations.archived,
    ];
    const inTrash = [
      ...webConversations.inTrash,
      ...instagramConversations.inTrash,
      ...facebookConversations.inTrash,
      ...linkedinConversations.inTrash,
      ...linkedinBusinessConversations.inTrash,
      ...whatsappConversations.inTrash,
    ];
    return {
      active,
      archived,
      inTrash,
    };
  }, [
    facebookConversations,
    linkedinConversations,
    linkedinBusinessConversations,
    webConversations,
    instagramConversations,
    whatsappConversations,
  ]);

  const conversations: Conversations = useMemo(() => {
    return {
      instagram: instagramConversations,
      facebook: facebookConversations,
      linkedin: linkedinConversations,
      linkedinBusiness: linkedinBusinessConversations,
      whatsapp: whatsappConversations,
      web: webConversations,
      all: getAllConversations(),
    };
  }, [
    facebookConversations,
    linkedinConversations,
    linkedinBusinessConversations,
    webConversations,
    instagramConversations,
    whatsappConversations,
    getAllConversations,
  ]);
  const {
    isModal,
    turnOffModal,
    turnOnModal,
    getModalVisibility,
    getModalContent,
    isLoading,
    turnOffInternalModal,
  } = useGlobalCore();
  useEffect(() => {
    if (
      !companyPlan?.rules.allowWebChat ||
      (!companyRole?.chat &&
        !companyRole?.owner &&
        !companyRole?.admin &&
        !companyRole?.agent &&
        !isAdmin &&
        !isReseller)
    )
      goToSocial();
  }, [companyPlan, companyRole, isAdmin, isReseller, goToSocial]);

  useEffect(() => {
    const w = window as Window & { $crisp?: unknown[] };
    const $crisp = w.$crisp;
    if (!Array.isArray($crisp)) {
      return;
    }

    const hideCrisp = () => {
      $crisp.push(["do", "chat:hide"]);
    };

    hideCrisp();

    const onSessionLoaded = () => {
      hideCrisp();
    };
    $crisp.push(["on", "session:loaded", onSessionLoaded]);

    const t1 = window.setTimeout(hideCrisp, 400);
    const t2 = window.setTimeout(hideCrisp, 1500);

    document.body.classList.add("webchat-hide-crisp");

    return () => {
      document.body.classList.remove("webchat-hide-crisp");
      window.clearTimeout(t1);
      window.clearTimeout(t2);
      $crisp.push(["off", "session:loaded"]);
      $crisp.push(["do", "chat:show"]);
    };
  }, []);

  useEffect(() => {
    getContactStates(company?.id!);
  }, [company?.id, getContactStates]);

  useEffect(() => {
    if (!company?.id) return;
    getMeSocialSettings(company.id);
    getFacebookSettings(company.id);
  }, [company?.id, getMeSocialSettings, getFacebookSettings]);

  useEffect(() => {
    getChatTemplates(company?.id!);
  }, [company?.id, getChatTemplates]);

  useEffect(() => {
    switch (selectedSocial) {
      case "all":
        getConversations(company?.id!, contactStateRequest);
        if (facebookMessagingConnected) {
          getFacebookConversations(company?.id!);
        }
        if (socialSettings?.whatsapp) {
          getWhatsappConversations(company?.id!, contactStateRequest);
        }
        if (isInstagramMessagingConnected(socialSettings)) {
          getInstagramConversations(company?.id!);
        }
        if (socialSettings?.linkedIn) {
          getLinkedinConversations(company?.id!);
        }
        if (socialSettings?.linkedInBusiness) {
          getLinkedinBusinessConversations(company?.id!);
        }
        break;
      case "instagram":
        if (isInstagramMessagingConnected(socialSettings)) {
          getInstagramConversations(company?.id!);
        }
        break;
      case "facebook":
        if (facebookMessagingConnected) {
          getFacebookConversations(company?.id!);
        }
        break;
      case "whatsapp":
        if (socialSettings?.whatsapp) {
          getWhatsappConversations(company?.id!, contactStateRequest);
        }
        break;
      case "linkedin":
        if (socialSettings?.linkedIn) {
          getLinkedinConversations(company?.id!);
        }
        break;
      case "linkedinBusiness":
        if (socialSettings?.linkedInBusiness) {
          getLinkedinBusinessConversations(company?.id!);
        }
        break;
      default:
        getConversations(company?.id!, contactStateRequest);
        break;
    }
  }, [
    getConversations,
    getWhatsappConversations,
    getFacebookConversations,
    getInstagramConversations,
    company?.id,
    contactStateRequest,
    socialSettings?.instagram,
    socialSettings?.instagramLogin,
    socialSettings?.instagramMensseger,
    socialSettings?.whatsapp,
    facebookMessagingConnected,
    isInstagramMessagingConnected(socialSettings),
    socialSettings?.linkedIn,
    socialSettings?.linkedInBusiness,
    getLinkedinConversations,
    getLinkedinBusinessConversations,
    selectedSocial,
  ]);

  useEffect(() => {
    const reconnected =
      wasSocketDisconnected.current && !disconnected && company?.id;
    wasSocketDisconnected.current = disconnected;

    if (reconnected && isInstagramMessagingConnected(socialSettings)) {
      syncInstagramInboxSilent(company.id, instagramSelected?.id ?? null);
    }
  }, [
    disconnected,
    company?.id,
    socialSettings,
    instagramSelected?.id,
    syncInstagramInboxSilent,
  ]);

  const onClickAvatar = useCallback(
    (contact) => {
      getContactTags(company?.id!);
      getCommentTypes(company?.id!);
      getContactStates(company?.id!);
      getBusinessBoards(company?.id!);
      getContact(company?.id!, contact.id);
      getTasksByContact(company?.id!, { contactId: contact.id });
      turnOnModal(
        CONTACT_EXPAND_EDIT_FORM,
        <EditContactForm
          closeOnUpdating={false}
          onDelete={async () => {
            await deleteContacts(company?.id!, [contact.id]);
            await getConversations(company?.id!, contactStateRequest);

            turnOffModal(CONTACT_EXPAND_EDIT_FORM);
          }}
          onUpdate={async () => {
            await getConversations(company?.id!, contactStateRequest);
            if (socialSettings?.whatsapp) {
              await getWhatsappConversations(company?.id!, contactStateRequest);
            }
            selectWebConversation(selected?.id!);
            selectWebConversation(selected?.id!);
          }}
        />,
        Position.LEFT
      );
    },
    [
      company,
      selected,
      turnOnModal,
      getTasksByContact,
      getWhatsappConversations,
      socialSettings?.whatsapp,
      contactStateRequest,
      getCommentTypes,
      turnOffModal,
      getConversations,
      deleteContacts,
      getContactTags,
      selectWebConversation,
      getBusinessBoards,
      getContactStates,
      getContact,
    ]
  );

  /** En vista "all" se usa la selección actual; al filtrar por canal solo cuenta ese canal (evita que Web quede por encima del filtro). */
  const selectedConv = useMemo(() => {
    switch (selectedSocial) {
      case "all":
        return (
          selected ||
          facebookSelected ||
          instagramSelected ||
          whatsappSelected ||
          linkedinSelected ||
          linkedinBusinessSelected
        );
      case "web":
        return selected ?? null;
      case "facebook":
        return facebookSelected ?? null;
      case "instagram":
        return instagramSelected ?? null;
      case "whatsapp":
        return whatsappSelected ?? null;
      case "linkedin":
        return linkedinSelected ?? null;
      case "linkedinBusiness":
        return linkedinBusinessSelected ?? null;
      default:
        return null;
    }
  }, [
    selectedSocial,
    selected,
    facebookSelected,
    instagramSelected,
    whatsappSelected,
    linkedinSelected,
    linkedinBusinessSelected,
  ]);

  const selectedPanelContacts = useMemo((): ConversationContact[] => {
    if (!selectedConv?.participants?.length) return [];
    const contact = selectedConv.participants[0];
    const participantId = String(contact.id);

    const messageAvatar = (() => {
      switch (selectedConv.type) {
        case "instagram":
          return instagramMessages.find(
            (message) => String(message.from.id) === participantId
          )?.from.avatarUrl;
        case "facebook":
          return facebookMessages.find(
            (message) => String(message.from.id) === participantId
          )?.from.avatarUrl;
        case "whatsapp":
          return whatsappMessages.find(
            (message) => String(message.from.id) === participantId
          )?.from.avatarUrl;
        case "linkedin":
          return linkedinMessages.find(
            (message) => String(message.from.id) === participantId
          )?.from.avatarUrl;
        case "linkedinBusiness":
          return linkedinBusinessMessages.find(
            (message) => String(message.from.id) === participantId
          )?.from.avatarUrl;
        default:
          return undefined;
      }
    })();

    const avatarUrl = pickBestAvatar(contact.avatarUrl, messageAvatar);
    if (!avatarUrl || avatarUrl === contact.avatarUrl) {
      return selectedConv.participants;
    }
    return [{ ...contact, avatarUrl }, ...selectedConv.participants.slice(1)];
  }, [
    selectedConv,
    instagramMessages,
    facebookMessages,
    whatsappMessages,
    linkedinMessages,
    linkedinBusinessMessages,
  ]);

  const chatForm = useMemo(() => {
    if (!selectedConv) return undefined;
    switch (selectedConv.type) {
      case "web":
        return <WebChatForm />;
      case "facebook":
        return (
          <SocialChatForm
            selected={facebookSelected}
            messages={facebookMessages}
            getMessages={getFacebookMessages}
            postMessage={({ companyId, conversationId, recipientId, content }) =>
              postFacebookMessage(companyId, conversationId, recipientId, content)
            }
            postSeenMessage={postFacebookSeenMessage}
          />
        );
      case "instagram":
        return (
          <SocialChatForm
            selected={instagramSelected}
            messages={instagramMessages}
            getMessages={getInstagramMessages}
            postMessage={(props) =>
              postInstagramMessage(
                props.companyId,
                props.conversationId,
                props.recipientId,
                props.content
              )
            }
          />
        );
      case "whatsapp":
        return (
          <SocialChatForm
            selected={whatsappSelected}
            messages={whatsappMessages}
            getMessages={getWhatsappMessages}
            postMessage={(props) =>
              postWhatsappMessage(
                props.companyId,
                props.conversationId,
                props.recipientId,
                props.content,
                props.type,
                props.attachments
              )
            }
          />
        );
      case "linkedin":
        return (
          <SocialChatForm
            selected={linkedinSelected}
            messages={linkedinMessages}
            getMessages={getLinkedinMessages}
            postMessage={(props) =>
              postLinkedinMessage(
                props.companyId,
                props.conversationId,
                props.recipientId,
                props.content
              )
            }
          />
        );
      case "linkedinBusiness":
        return (
          <SocialChatForm
            selected={linkedinBusinessSelected}
            messages={linkedinBusinessMessages}
            getMessages={getLinkedinBusinessMessages}
            postMessage={(props) =>
              postLinkedinBusinessMessage(
                props.companyId,
                props.conversationId,
                props.recipientId,
                props.content
              )
            }
          />
        );
      default:
        return undefined;
    }
  }, [
    selectedConv,
    selected,
    instagramMessages,
    instagramSelected,
    facebookMessages,
    facebookSelected,
    whatsappMessages,
    whatsappSelected,
    linkedinMessages,
    linkedinSelected,
    linkedinBusinessMessages,
    linkedinBusinessSelected,
    getFacebookMessages,
    postFacebookMessage,
    postFacebookSeenMessage,
    getInstagramMessages,
    postInstagramMessage,
    getLinkedinMessages,
    postLinkedinMessage,
    getLinkedinBusinessMessages,
    postLinkedinBusinessMessage,
    getWhatsappMessages,
    postWhatsappMessage,
  ]);

  const onSelect = useCallback(
    (conversation) => {
      if (conversation.type === "web") {
        selectWebConversation(conversation.id);
        selectFacebookConversation(null);
        selectInstagramConversation(null);
        selectWhatsappConversation(null);
        selectLinkedinConversation(null);
        selectLinkedinBusinessConversation(null);
      }
      if (conversation.type === "facebook") {
        selectFacebookConversation(conversation.id);
        selectWebConversation(null);
        selectInstagramConversation(null);
        selectWhatsappConversation(null);
        selectLinkedinConversation(null);
        selectLinkedinBusinessConversation(null);
      }
      if (conversation.type === "instagram") {
        selectInstagramConversation(conversation.id);
        selectFacebookConversation(null);
        selectWhatsappConversation(null);
        selectWebConversation(null);
        selectLinkedinConversation(null);
        selectLinkedinBusinessConversation(null);
      }
      if (conversation.type === "whatsapp") {
        selectWhatsappConversation(conversation.id);
        selectWebConversation(null);
        selectFacebookConversation(null);
        selectInstagramConversation(null);
        selectLinkedinConversation(null);
        selectLinkedinBusinessConversation(null);
      }
      if (conversation.type === "linkedin") {
        selectLinkedinConversation(conversation.id);
        selectWebConversation(null);
        selectFacebookConversation(null);
        selectInstagramConversation(null);
        selectWhatsappConversation(null);
        selectLinkedinBusinessConversation(null);
      }
      if (conversation.type === "linkedinBusiness") {
        selectLinkedinBusinessConversation(conversation.id);
        selectWebConversation(null);
        selectFacebookConversation(null);
        selectInstagramConversation(null);
        selectWhatsappConversation(null);
        selectLinkedinConversation(null);
      }
    },
    [
      selectWebConversation,
      selectFacebookConversation,
      selectInstagramConversation,
      selectWhatsappConversation,
      selectLinkedinConversation,
      selectLinkedinBusinessConversation,
    ]
  );

  const totalConversationsCount = useMemo(() => {
    const all = conversations.all;
    return (
      all.active.length + all.archived.length + all.inTrash.length
    );
  }, [conversations]);

  return (
    <Layout className="h-100 min-h-0 webchat-layout-root">
      <Content className="webchat-page-root h-100 min-h-0">
        <div className={cn("webchat-inbox-shell w-100 h-100 d-flex flex-column min-h-0", {
          "webchat-inbox-shell--thread-open": Boolean(selectedConv),
        })}>
          <Container
            className="webchat-page-container p-0 d-flex flex-column flex-grow-1 min-h-0"
            fluid
          >
            <Row className="g-0 flex-lg-nowrap flex-grow-1 min-h-0 h-100">
              <Col
                xs="12"
                lg="5"
                xl="4"
                className="order-1 webchat-inbox-pane d-flex flex-column min-h-0"
              >
                <WebChatInboxToolbar
                  conversations={conversations}
                  searchText={filter}
                  onSearchChange={setFilter}
                  listFilter={listFilter}
                  onListFilterChange={setListFilter}
                  onOpenAdvancedFilters={openFiltersModal}
                />
                <ConversationList
                  filter={filter}
                  listFilter={listFilter}
                  activeConversationId={selectedConv?.id ?? null}
                  conversations={conversations}
                  onSelect={(conversation: string) => onSelect(conversation)}
                />
              </Col>
              <Col
                xs="12"
                lg="7"
                xl="8"
                className="order-2 webchat-thread-pane d-flex flex-column min-h-0"
              >
                <div className="webchat-thread-inner p-0 z-index-50 h-100 d-flex flex-column flex-grow-1 min-h-0 w-100">
                  {!selectedConv ? (
                    <div className="webchat-thread-placeholder d-flex flex-grow-1 align-items-center justify-content-center min-h-0 w-100 py-4">
                      {totalConversationsCount === 0 ? (
                        <WebChatEmptyState />
                      ) : (
                        <NoData
                          className="webchat-thread-no-data"
                          image="generic2"
                          text={intl.formatMessage({
                            id: "app.chat.selectContactToStart",
                          })}
                          horizontal
                        />
                      )}
                    </div>
                  ) : (
                    <main className="webchat-thread-main d-flex flex-column flex-grow-1 min-h-0 w-100 h-100">
                      <PanelChatTitle
                        companyId={company?.id!}
                        color="#d0d0d0d0"
                        type={selectedConv.type}
                        contacts={selectedPanelContacts}
                        avatarAction={onClickAvatar}
                        variant="inbox"
                      />
                      <div className="webchat-thread-form-root d-flex flex-column flex-grow-1 min-h-0 w-100">
                        {chatForm}
                      </div>
                    </main>
                  )}
                </div>
              </Col>
            </Row>
          </Container>
        </div>
      </Content>

      <TaskSideModals />
      <ContactSideModals />
      <CompanySideModals />
      <BoardSideModals />
      <EventSideModals />
      <EmailSideModals />

      <SideModal
        active={isModal(BOARD_CARD_MODAL)}
        visible={getModalVisibility(BOARD_CARD_MODAL)}
        onClose={() => {
          turnOffModal(BOARD_CARD_MODAL);
          turnOffInternalModal(CARD_PRODUCT);
          cleanCard();
        }}
        showSppiner={isLoading(BOARD_CARD_LOADING)}
        showError={errorCode === Errors.CardLoad}
      >
        {getModalContent(BOARD_CARD_MODAL)}
      </SideModal>
      <SideModal
        active={isModal(TEMPLATE_CHAT_FORM)}
        visible={getModalVisibility(TEMPLATE_CHAT_FORM)}
        onClose={() => {
          turnOffModal(TEMPLATE_CHAT_FORM);
        }}
        left
      >
        <TemplateChatForm />
      </SideModal>
      <SideModal
        active={isModal(SOCIAL_CHAT_FILTERS_MODAL)}
        visible={getModalVisibility(SOCIAL_CHAT_FILTERS_MODAL)}
        onClose={() => {
          turnOffModal(SOCIAL_CHAT_FILTERS_MODAL);
        }}
        left={false}
      >
        <ChatBarFilter conversations={conversations} />
      </SideModal>
      <SideModal
        active={isModal(EMOJI_FORM)}
        visible={getModalVisibility(EMOJI_FORM)}
        onClose={() => turnOffModal(EMOJI_FORM)}
      >
        {getModalContent(EMOJI_FORM)}
      </SideModal>
      <SideModal
        active={isModal(CHAT_FILTERS_MODAL)}
        visible={getModalVisibility(CHAT_FILTERS_MODAL)}
        onClose={() => turnOffModal(CHAT_FILTERS_MODAL)}
      >
        <ChatFilterModal conversations={conversations} />
      </SideModal>
    </Layout>
  );
}
